// Considered: DeskFindingRowMolecule — rejected. Findings are flat text + severity; a rule row
// must render a two-part if/then sentence built from a condition tree (`applyWhen` gates
// `condition`), which the finding row has no shape for. This row DOES reuse the finding row's
// `SEVERITY_TONE` map (error→danger, warning→warn, info→info) from the StatusBadge module,
// which both this row and DeskFindingRowMolecule import — the map was duplicated privately in
// each until review pass 28 lifted it, and a severity must not read as two conditions in two
// places. See spec Design System Reuse Audit.
//
// CLOSED summary row, same fix shape as `relationshipListRow` (EAINT-897 Phase 1c). The
// contract (`type-composer.jsx:1564-1593`) never renders name/severity/enabled as
// always-editable inline controls: the row is a closed line (name, severity word, dormant/
// switched-off markers, the if/then sentence) and editing happens in `RuleSheetMolecule`,
// opened by acting on the row. `onEdit` is only ever supplied in `mode="edit"`.
import React from 'react';
import { Trans, useTranslation } from 'localization';
import type { BusinessRule, BusinessRuleCondition, BusinessRuleExpression, BusinessRuleSeverity } from 'types/businessRules';
import { SEVERITY_TONE } from '../../atoms/StatusBadge';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import TranslationCoverageChipAtom from '../../atoms/TranslationCoverageChip/TranslationCoverageChipAtom';
import type { TypeFieldRowMode } from './TypeFieldRowMolecule';
import './TypeRuleRowMolecule.scss';

export interface TypeRuleRowMoleculeProps {
  rule: BusinessRule;
  className?: string;
  /** @see TypeFieldRowMode. Only `'edit'` supplies `onEdit`'s affordance a role. */
  mode?: TypeFieldRowMode;
  /** Opens the rule's editor sheet. Omitted for a read-only/locked/disabled row. */
  onEdit?: () => void;
  /** Whether that sheet is open under this row — turns the disclosure chevron. */
  isOpen?: boolean;
  /**
   * How many of the rule's problems the engine cannot get past
   * (`blockingProblemCount`). An inherited rule never shows a count — it is not
   * this type's to fix, and the marker would offer no action.
   */
  problemCount?: number;
  /** The ancestor type this rule was declared on, when it is inherited. */
  inheritedFromName?: string;
  /**
   * Opens the type's Languages section, scrolled to this rule's message
   * (`type-composer.jsx:1578`, `LocChip`) — the same translation-coverage chip a
   * declared property or relationship name carries. An inherited rule shows its
   * provenance instead (`rules.r-from`): the message is the ancestor's to translate.
   */
  onOpenTranslations?: () => void;
}

/** `type-composer.jsx:1576` — the severity word shown on the closed row, contract datum `rules.blocks`. */
const SEVERITY_WORD_KEY: Record<BusinessRuleSeverity, string> = {
  error: 'studio.typeEditor.businessRules.severityWord.blocks',
  warning: 'studio.typeEditor.businessRules.severityWord.warns',
  info: 'studio.typeEditor.businessRules.severityWord.notes',
};

/**
 * The severity WORD's own badge (`rules.blocks`) reads opposite to `SEVERITY_TONE`
 * (used for the row's own `--severity-*` class below): `studio.css:741-742`
 * (`.t-line-req`) colours the blocking word ("blocks", severity `error`) amber, and
 * every softer word ("warns"/"notes") a muted blue-grey (`[data-soft]`) — never red.
 * A private map because `DeskFindingRowMolecule`'s `SEVERITY_TONE` import is a
 * different badge with the opposite semantics; conflating them would flip one of
 * the two the next time someone "simplifies" the duplicate-looking maps.
 */
const SEVERITY_WORD_TONE: Record<BusinessRuleSeverity, TagTone> = {
  error: 'warn',
  warning: 'neutral',
  info: 'neutral',
};

function isCondition(
  expression: BusinessRuleExpression | BusinessRuleCondition
): expression is BusinessRuleCondition {
  return Array.isArray((expression as BusinessRuleCondition).expressions);
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Renders one expression as a clause through an interpolated key rather than by
 * concatenation. Word order is not stable across locales (French moves negation
 * and comparative phrasing), so the template has to own the ordering —
 * localization.md Rule 4. Unary operators (`exists` / `is_true` / `is_false`)
 * take a separate key because they have no value slot to fill.
 */
function describeExpression(expression: BusinessRuleExpression, t: Translate): string {
  const operatorWord = t(`studio.typeEditor.businessRules.operator.${expression.operator}`);
  if (expression.operator === 'exists' || expression.operator === 'is_true' || expression.operator === 'is_false') {
    return t('studio.typeEditor.businessRules.expressionUnary', {
      property: expression.property,
      operator: operatorWord,
    });
  }
  const value =
    (expression.operator === 'in' || expression.operator === 'not_in') && Array.isArray(expression.value)
      ? expression.value.join(t('studio.typeEditor.businessRules.valueSeparator'))
      : String(expression.value);
  return t('studio.typeEditor.businessRules.expression', {
    property: expression.property,
    operator: operatorWord,
    value,
  });
}

function describeCondition(condition: BusinessRuleCondition, t: Translate): string {
  // Nothing written yet — the contract's placeholder (`rules.x`, `type-composer.jsx:1590`),
  // not the word "Always". "Always" is the GATE's answer and already sits at the head of
  // the sentence, so returning it here read as "Always, Always." Joining an empty list
  // instead produced an empty sentence, which said nothing at all.
  if (!condition.expressions?.length) return t('studio.typeEditor.businessRules.unwritten');
  const joiner = t(`studio.typeEditor.businessRules.joiner.${condition.operator}`);
  return condition.expressions
    .map((entry) =>
      isCondition(entry)
        ? t('studio.typeEditor.businessRules.nestedGroup', { group: describeCondition(entry, t) })
        : describeExpression(entry, t)
    )
    .join(t('studio.typeEditor.businessRules.joinerWrap', { joiner }));
}

/** Shared with `RuleSheetMolecule`'s read-only condition note — one readback, not two. */
export function ruleSentence(rule: BusinessRule, t: Translate): string {
  const requirement = describeCondition(rule.condition, t);
  return rule.applyWhen
    ? t('studio.typeEditor.businessRules.ifThen', {
        gate: describeCondition(rule.applyWhen, t),
        requirement,
      })
    : requirement;
}

/**
 * The same readback, rendered — `type-composer.jsx:1589-1591`.
 *
 * The contract emphasises the two words that carry the rule's shape (`If` … `then`,
 * or `Always`), so they are elements, not text. `Trans` owns the ordering: the
 * emphasis sits at a different point in the sentence in French, and building the
 * fragment by concatenation here would hardcode English word order (localization.md
 * Rule 4). `ruleSentence` above stays the plain-string form for places that need one
 * (the sheet's summary, aria labels).
 */
export const RuleSentence: React.FC<{
  rule: BusinessRule;
  /**
   * The same readback is spoken twice — on the closed row and at the head of the open
   * editor — and the decomposition walks each occurrence separately, so the emphasised
   * words carry the caller's contract ids. The row's are the defaults.
   */
  ifDatum?: string;
  thenDatum?: string;
  alwaysDatum?: string;
}> = ({ rule, ifDatum = 'rules.if', thenDatum = 'rules.then', alwaysDatum = 'rules.always' }) => {
  const { t } = useTranslation();
  const requirement = describeCondition(rule.condition, t);
  const gated = !!rule.applyWhen?.expressions?.length;
  return gated ? (
    <Trans
      i18nKey="studio.typeEditor.businessRules.ifThenRendered"
      values={{ gate: describeCondition(rule.applyWhen as BusinessRuleCondition, t), requirement }}
      components={{
        if: <strong data-datum={ifDatum} data-kind="strong" />,
        then: <strong data-datum={thenDatum} data-kind="strong" />,
      }}
    />
  ) : (
    <Trans
      i18nKey="studio.typeEditor.businessRules.alwaysRendered"
      values={{ requirement }}
      components={{ always: <strong data-datum={alwaysDatum} data-kind="strong" /> }}
    />
  );
};

const TypeRuleRowMolecule: React.FC<TypeRuleRowMoleculeProps> = ({
  rule,
  className = '',
  mode = 'disabled',
  onEdit,
  isOpen = false,
  problemCount = 0,
  inheritedFromName,
  onOpenTranslations,
}) => {
  const { t } = useTranslation();
  const rootClasses = ['type-rule-row', className].filter(Boolean).join(' ');
  const name = rule.name || (rule.nameKey ? t(rule.nameKey) : '') || t('studio.typeEditor.businessRules.untitledRule');
  const isDormant = rule.executeOn.includes('flow_transition');
  const isOff = rule.enabled === false;
  const isInherited = rule.is_inherited === true;
  // `type-composer.jsx:1562` — `bad = !r.inherited && ruleTrouble(r)`. An inherited
  // rule's problems belong to the type that declared it; counting them here would
  // mark a row whose fix is somewhere else entirely.
  const toFix = isInherited ? 0 : problemCount;

  // The name/severity/markers/chevron cluster is the disclosure trigger; the translation-
  // coverage / inherited-from chip is its own real `<button>` (TranslationCoverageChipAtom)
  // and must sit outside it — a `<button>` cannot contain another interactive control
  // (axe `nested-interactive`; the row previously used `role="button"` on the outer `<div>`
  // wrapping that chip, same defect class as the SegmentedControlAtom `asTabs` fix).
  const triggerContent = (
    <>
      <span className="type-rule-row__name" data-datum="rules.untitled-rule" data-kind="label">
        {name}
      </span>
      <TagAtom
        tone={SEVERITY_WORD_TONE[rule.severity]}
        shape="chip"
        size="small"
        label={t(SEVERITY_WORD_KEY[rule.severity])}
        data-datum="rules.blocks"
        data-kind="chip"
      />
      {/*
        `type-composer.jsx:1578`. A rule that fires on arc transitions is
        accepted and stored, but the checker never reaches it — so it simply
        does not run. Without this the rule reads as active, and the author has
        no way to discover otherwise; the reason travels with the marker rather
        than living in a release note.
      */}
      {isDormant && (
        <>
          <TagAtom
            tone="warn"
            size="small"
            label={t('studio.typeEditor.businessRules.dormant')}
            data-testid="type-rule-row-dormant"
            data-datum="rules.dormant"
            data-kind="chip"
          />
          {/*
            The prototype hangs the reason off a `title` tooltip. A tooltip is
            not reachable by keyboard and is invisible on touch, and this is
            precisely a state whose reason must not be hidden (ux-craft I2), so
            it renders as text beside the marker instead.
          */}
          <span className="type-rule-row__dormant-reason">
            {t('studio.typeEditor.businessRules.arcTransitionsNotReached')}
          </span>
        </>
      )}
      {isOff && (
        <TagAtom
          tone="outline"
          size="small"
          label={t('studio.typeEditor.businessRules.switchedOff')}
          data-datum="rules.switched-off"
          data-kind="chip"
        />
      )}
      {toFix > 0 && (
        <TagAtom
          tone="danger"
          size="small"
          label={t('studio.typeEditor.businessRules.toFix', { count: toFix })}
          data-testid="type-rule-row-to-fix"
          data-datum="rules.to-fix"
          data-kind="chip"
        />
      )}
    </>
  );

  const provenanceChip = isInherited ? (
    <TagAtom
      tone="outline"
      size="small"
      label={inheritedFromName ?? t('studio.typeEditor.row.unknownAncestor')}
      data-testid="type-rule-row-inherited-from"
      data-datum="rules.r-from"
      data-kind="chip"
    />
  ) : (
    /*
      Translation coverage on this type's OWN rule message (`type-composer.jsx:1578`,
      `LocChip`) — the same chip a declared property, relationship name, or the type
      name carries. An inherited rule's message is the ancestor's to translate, so it
      shows provenance (`rules.r-from`) instead.
    */
    onOpenTranslations && (
      <TranslationCoverageChipAtom
        done={0}
        total={name.trim() ? 1 : 0}
        label={t('studio.typeEditor.businessRules.translations', { name })}
        onClick={onOpenTranslations}
        data-datum="rules.chip"
        data-testid={`type-rule-row-translations-${rule.id}`}
      />
    )
  );

  return (
    <div
      className={`${rootClasses} type-rule-row--severity-${SEVERITY_TONE[rule.severity]}`}
      data-container="list-item"
      data-testid={`type-rule-row-${rule.id}`}
    >
      <div className="type-rule-row__header">
        {onEdit ? (
          <button
            type="button"
            className="type-rule-row__trigger"
            data-testid={`type-rule-row-${rule.id}-trigger`}
            onClick={onEdit}
            aria-label={t('studio.typeEditor.businessRules.editRowAria', { name })}
            aria-expanded={isOpen}
          >
            {triggerContent}
            {/*
              The disclosure cue (`type-composer.jsx:1586`): a row that opens says so.
            */}
            <i
              className={`type-rule-row__chevron pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`}
              aria-hidden="true"
            />
          </button>
        ) : (
          triggerContent
        )}
        {provenanceChip}
      </div>
      <p className="type-rule-row__sentence" data-datum="rules.x" data-kind="text">
        <RuleSentence rule={rule} />
      </p>
    </div>
  );
};

TypeRuleRowMolecule.displayName = 'TypeRuleRowMolecule';

export default TypeRuleRowMolecule;
